// VoiceSpectrum.Web.T2 · v10.0.10.10 · 2026-05-19 · VoiceSpectrum AI
//
// Input + Textarea + Select + FormField + SecretInput — uniform v2.2 form patterns.

import {
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  useId,
  useState,
} from 'react';

// ── FormField (label + control + helper/error wrapper) ────────────────────────
export interface FormFieldProps {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  dirty?: boolean;
  invalid?: boolean;
  children: ReactNode;
}

export function FormField({
  label, helper, error, required, htmlFor, dirty, invalid, children,
}: FormFieldProps) {
  const cls = [
    'field',
    dirty ? 'is-dirty' : '',
    invalid || error ? 'is-invalid' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      {label && (
        <label htmlFor={htmlFor} className={`field-label ${required ? 'field-required' : ''}`}>
          {label}
        </label>
      )}
      {children}
      {error
        ? <div className="field-error">{error}</div>
        : helper ? <div className="field-helper">{helper}</div> : null}
    </div>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  dirty?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, helper, error, required, dirty, id, ...rest }, ref,
) {
  const auto = useId();
  const inputId = id ?? `inp-${auto}`;
  return (
    <FormField label={label} helper={helper} error={error} required={required} htmlFor={inputId} dirty={dirty}>
      <input ref={ref} id={inputId} {...rest} />
    </FormField>
  );
});

// ── Textarea ──────────────────────────────────────────────────────────────────
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  dirty?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, helper, error, required, dirty, id, ...rest }, ref,
) {
  const auto = useId();
  const inputId = id ?? `txt-${auto}`;
  return (
    <FormField label={label} helper={helper} error={error} required={required} htmlFor={inputId} dirty={dirty}>
      <textarea ref={ref} id={inputId} {...rest} />
    </FormField>
  );
});

// ── Select ────────────────────────────────────────────────────────────────────
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  dirty?: boolean;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, helper, error, required, dirty, id, options, ...rest }, ref,
) {
  const auto = useId();
  const inputId = id ?? `sel-${auto}`;
  return (
    <FormField label={label} helper={helper} error={error} required={required} htmlFor={inputId} dirty={dirty}>
      <select ref={ref} id={inputId} {...rest}>
        {options.map(o => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </FormField>
  );
});

// ── SecretInput (password-style field with Show/Hide toggle) ──────────────────
// When `configured` is true and `editing` is false, the field shows a read-only
// chip "•••• configured (stored at tenant level)" with a Replace button — this is
// the pattern Settings uses for already-saved tenant-level API keys.
export interface SecretInputProps extends Omit<InputProps, 'type'> {
  configured?: boolean;
  onReplaceClick?: () => void;
}

export function SecretInput({
  label, helper, error, required, dirty, id,
  configured = false, onReplaceClick,
  ...rest
}: SecretInputProps) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(!configured);
  const auto = useId();
  const inputId = id ?? `sec-${auto}`;

  if (configured && !editing) {
    return (
      <FormField label={label} helper={helper} error={error} required={required} htmlFor={inputId} dirty={dirty}>
        <div className="field-readonly">
          <span>•••• configured</span>
          <em>(stored at organization level)</em>
          <span className="grow" />
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => { setEditing(true); onReplaceClick?.(); }}
          >
            Replace
          </button>
        </div>
      </FormField>
    );
  }

  return (
    <FormField label={label} helper={helper} error={error} required={required} htmlFor={inputId} dirty={dirty}>
      <div className="secret-row">
        <input id={inputId} type={show ? 'text' : 'password'} {...rest} />
        <button type="button" className="secret-toggle" onClick={() => setShow(s => !s)}>
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </FormField>
  );
}
